import { FC, Fragment, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { SpaEntityType } from 'shared-types/Spa';
import { sum } from 'shared-utils';
import { useTranslations } from '@lang';
import { ItemDescription } from '@features/List/ListItem.styles';
import rewriteHttp from '@utils/rewriteHttp';
import { FormStep, PackagesFormValues } from '../../../types';
import { ExperienceDetailsProps } from '../../ExperienceDetails/ExperienceDetails';
import { ExperienceDetails } from '../../ExperienceDetails';
import { ExperienceDetailsClasses } from '../../ExperienceDetails/ExperienceDetailsClasses';
import { usePackageConfiguration } from '../../../contexts';
import { getTitleForExperienceType } from '../helpers/getTitleForExperienceType';
import { SpaTypeTitle } from '../ExperiencesGroup.styles';
import { ExperienceType } from '@features/DynamicPackagesConfigurationWizard/types';

interface Props {
  selectionGroup: Omit<FormStep, 'step' | 'type'>;
  allOptionsIncluded?: boolean;
  spaExperiences: Array<Partial<SpaEntityType> & { startTime?: string }>;
  plusMinusSpaOption: ExperienceDetailsProps['plusMinusSpaOption'];
  totalGuests: number;
  dayNumber: number;
  renderExperienceItem: (props: {
    exp: Partial<SpaEntityType>;
    experienceDetailProps: ExperienceDetailsProps;
    imageUrl: string;
    isClasses: boolean;
  }) => ReactNode;
  renderGroup: (content: ReactNode) => ReactNode;
}

export const ExperiencesGroupOptionsBase: FC<Props> = ({
  selectionGroup,
  allOptionsIncluded,
  spaExperiences,
  plusMinusSpaOption,
  totalGuests,
  dayNumber,
  renderExperienceItem,
  renderGroup,
}) => {
  const { property } = usePackageConfiguration();
  const { t } = useTranslations();
  const { watch } = useFormContext<PackagesFormValues>();
  const selectedExperiences = watch('selectedExperiences');

  const selectionsInOptionGroup = (selectedExperiences || []).filter(
    (exp) => exp.optionGroupId === selectionGroup.optionGroupId && exp.dayNumber === dayNumber
  );

  const totalNumberOfSelectionsInGroup = sum(selectionsInOptionGroup, (option) => option.count);
  const maxSelectionsForGroup = totalGuests * (selectionGroup.selectionsRequired || 1);

  const treatments = spaExperiences.filter((exp) => exp.type === 'spa-treatments');
  const facilities = spaExperiences.filter((exp) => exp.type === 'spa-facilities');
  const classes = spaExperiences.filter((exp) => exp.type === 'spa-classes');

  function renderSpaExperiences(spaExps: Partial<SpaEntityType>[]) {
    return spaExps?.map((exp, index) => {
      const optionId = selectionGroup.options?.find(
        (g) => Number(g.id) === Number(exp.id)
      )?.optionId;
      const spaOptionCount =
        selectedExperiences?.find(
          (option) =>
            Number(option.id) === Number(exp?.id) &&
            option.optionId === optionId &&
            option.dayNumber === dayNumber
        )?.count || 0;

      const experienceDetailProps = {
        entityType: exp.type as ExperienceType,
        id: Number(exp?.id),
        language: property.defaultLanguage,
        propertyId: property.id,
        count: spaOptionCount || 0,
        plusMinusSpaOption: plusMinusSpaOption,
        disabledMinus: spaOptionCount === 0,
        disabledPlus:
          spaOptionCount >= totalGuests || totalNumberOfSelectionsInGroup >= maxSelectionsForGroup,
        allOptionsIncluded,
        unavailable: exp?.unAvailable,
        optionId: Number(optionId),
        spaEntity: exp,
      };

      const imageUrl = rewriteHttp(exp?.image?.url || exp?.imageGallery?.[0]?.url);
      const isClasses = exp.type === 'spa-classes';

      return (
        <Fragment key={exp.id ?? index}>
          {renderExperienceItem({
            exp,
            experienceDetailProps,
            imageUrl,
            isClasses,
          })}
        </Fragment>
      );
    });
  }

  const renderSection = (
    experiences: Partial<SpaEntityType>[],
    type: 'spa-facilities' | 'spa-treatments' | 'spa-classes'
  ) => {
    if (!experiences.length) return null;

    return (
      <>
        <SpaTypeTitle>{t(getTitleForExperienceType(type))}</SpaTypeTitle>
        {renderSpaExperiences(experiences)}
      </>
    );
  };

  return renderGroup(
    <>
      {renderSection(facilities, 'spa-facilities')}
      {renderSection(treatments, 'spa-treatments')}
      {renderSection(classes, 'spa-classes')}
    </>
  ) as JSX.Element;
};

export const renderExperienceDetailComponent = (
  experienceDetailProps: ExperienceDetailsProps,
  isClasses: boolean
) => {
  return (
    <ItemDescription>
      {isClasses ? (
        <ExperienceDetailsClasses {...experienceDetailProps} />
      ) : (
        <ExperienceDetails {...experienceDetailProps} />
      )}
    </ItemDescription>
  );
};
